import { Box, Card, CardContent, Collapse, TableCell, TableRow, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';
import CellSelector from '../Cells/CellSelector';
import CellViewer from '../Cells/CellViewer';
import QueryAdder from '../QueryAdder/QueryAdder';
import { createContext, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/AddBox';
import ToolTip from '@mui/material/Tooltip';
import { API, getTabColorHeaderMain, TrackerDataType } from '../../shared/constants/constants';
import { useLocation } from 'react-router-dom';
import { processCellsForAutoComplete } from '../../shared/functions/autoCompleteProcessing';
import AddLinkIcon from '@mui/icons-material/AddLink';
import UrlDialog from '../ServiceLog/UrlDialog';
import { useDispatch, useSelector } from 'react-redux';
import { openSnackbar } from '../AppSnackbar/snackbarSlice';
import { ContentLogKeys, EngagementKeys, IssueKeys, ServiceKeys } from '../../shared/constants/IssueAndServiceKeys';
import QueryAdderDrawer from '../QueryAdder/QueryAdderDrawer';
import EditCommsMaterialDialog from '../Cells/EditCommsMaterialDialog';
import EditCollapsedLiveLinkDialog from '../Cells/EditCollapsedLiveLinkDialog';
import { AddOnTypes } from '../../shared/constants/TenantFields';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { normalizeServiceId } from '../../shared/utils/serviceLogNormalization';

const useStyles = makeStyles(({ palette }) => ({
  hiddenCellCard: {
    flex: 1,
    alignSelf: 'flex-start',
    margin: '0',
    textAlign: 'center',
    border: 'none',
    borderRadius: 0,
    boxShadow: 'none',
  },
  cardContent: {
    padding: '0 !important',
    borderRadius: 0,
    border: 'none',
    boxShadow: 'none',
  },
  cardName: {
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: palette.whiteScaleDarker.main,
    padding: 8,
    color: palette.whiteScaleDarker.contrastText,
    fontWeight: 400,
  },
  cardData: {
    padding: 5,
    wordWrap: 'break-word',
  },
  hiddenColsContainer: {
    display: 'flex',
    width: '100%',
  },
  collapseWrapperInner: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    width: '100%',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
  },
}));

const hiddenColsMap = {
  MediaInteraction: [
    {
      name: IssueKeys.dueDate,
      sx: {
        flex: '0 1 5%',
      },
    },
    {
      name: IssueKeys.contactMethod,
      sx: {
        flex: '0 1 5%',
      },
    },
    {
      name: IssueKeys.dept,
      sx: {
        flex: '0 1 8%',
      },
    },
    {
      name: IssueKeys.keyMessaging,
      sx: {
        flex: '0 1 25%',
      },
    },
    {
      name: IssueKeys.response,
      sx: {
        flex: '0 1 25%',
      },
    },
    {
      name: IssueKeys.linkedService,
      sx: {
        flex: '0 1 12%',
      },
    },
    {
      name: IssueKeys.file,
      sx: {
        flex: '0 1 20%',
      },
    },
  ],
  ServiceLog: [
    {
      name: ServiceKeys.dept,
      sx: {
        flex: '0 1 10%',
      },
    },
    {
      name: ServiceKeys.keyMessaging,
      sx: {
        flex: '0 1 30%',
      },
    },
    {
      name: ServiceKeys.actions,
      sx: {
        flex: '0 1 30%',
      },
    },
    {
      name: ServiceKeys.linkedInteraction,
      sx: {
        flex: '0 1 15%',
      },
    },
    {
      name: ServiceKeys.file,
      sx: {
        flex: '0 1 25%',
      },
    },
  ],
  EngagementLog: [
    {
      name: EngagementKeys.dueDate,
      sx: {
        flex: '0 1 5%',
      },
    },
    {
      name: EngagementKeys.contactMethod,
      sx: {
        flex: '0 1 5%',
      },
    },
    {
      name: EngagementKeys.dept,
      sx: {
        flex: '0 1 8%',
      },
    },
    {
      name: EngagementKeys.notes,
      sx: {
        flex: '0 1 25%',
      },
    },
    {
      name: EngagementKeys.background,
      sx: {
        flex: '0 1 25%',
      },
    },
    {
      name: EngagementKeys.linkedService,
      sx: {
        flex: '0 1 12%',
      },
    },
    {
      name: EngagementKeys.file,
      sx: {
        flex: '0 1 20%',
      },
    },
  ],
  ContentLog: [
    {
      name: ContentLogKeys.otherNotes,
      sx: {
        flex: '0 1 14%',
      },
    },
    {
      name: ContentLogKeys.content,
      sx: {
        flex: '0 1 22%',
      },
    },
    {
      name: ContentLogKeys.background,
      sx: {
        flex: '0 1 22%',
      },
    },
    {
      name: ContentLogKeys.linkedService,
      sx: {
        flex: '0 1 12%',
      },
    },
    {
      name: ContentLogKeys.file,
      sx: {
        flex: '0 1 20%',
      },
    },
    {
      name: ContentLogKeys.liveLink,
      sx: {
        flex: '0 1 10%',
      },
    },
  ],
};

const FileUploadButton = ({ file, onChange }) => {
  return (
    <>
      <input type="file" multiple id="file" ref={file} onChange={onChange} style={{ display: 'none' }} />
      <ToolTip title="add file(s)">
        <label htmlFor="file" data-cy="file-upload-button">
          <IconButton onClick={() => file.current.click()} size="large">
            <AddIcon style={{ color: 'white' }} />
          </IconButton>
        </label>
      </ToolTip>
    </>
  );
};

const UrlUploadButton = ({ setFormOpen }) => {
  return (
    <>
      <ToolTip title="add link to web-hosted file">
        <IconButton onClick={() => setFormOpen(true)} size="large" data-cy="url-upload-button">
          <AddLinkIcon style={{ color: 'white' }} />
        </IconButton>
      </ToolTip>
    </>
  );
};

const LiveLinkUploadButton = ({ disabled, onAdd }) => {
  return (
    <ToolTip title={disabled ? 'live link already exists' : 'add new live link'}>
      <span>
        <IconButton onClick={onAdd} size="large" data-cy="live-link-upload-button" disabled={disabled}>
          <AddLinkIcon style={{ color: 'white' }} />
        </IconButton>
      </span>
    </ToolTip>
  );
};

LiveLinkUploadButton.propTypes = {
  disabled: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
};

export const FileProgressContext = createContext();

const isServicePath = (pathPrefix) => pathPrefix === 'service';

const TrackerCollapsedRow = ({
  cells,
  open,
  colSpan,
  dropdownTable,
  isEditMode,
  onEdit,
  type,
  linkedEventHandler,
  secondaryLinkedEventHandler,
  tertiaryLinkedEventHandler,
  id,
}) => {
  const { pathname, state } = useLocation();
  const theme = useTheme();
  const classes = useStyles({ tabColor: getTabColorHeaderMain(pathname, theme) });
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const user = useSelector((state) => state.user);
  const userTenantHasEngagementAddOn = user.addonControls[AddOnTypes.engagementLog];
  const userTenantHasContentLogAddOn = user.addonControls[AddOnTypes.contentLog];

  const file = useRef(null);
  const [fileProgress, setFileProgress] = useState();

  const [isUrlFormOpen, setIsUrlFormOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentFileToEdit, setCurrentFileToEdit] = useState(null);
  const [currentEditColumn, setCurrentEditColumn] = useState(null);

  const [isDragActive, setDragActive] = useState(false);

  const uploadFilesMutation = useMutation({
    mutationFn: async ({ pathPrefix, id, files, onProgress }) => {
      if (isServicePath(pathPrefix)) {
        const normalizedId = normalizeServiceId(id);
        if (!normalizedId) {
          throw new Error('invalid service file upload payload');
        }
        const headers = { 'content-type': 'multipart/form-data' };
        const formData = new FormData();
        files.forEach((file) => {
          formData.append('file', file);
        });

        return API.post(`/${pathPrefix}/${normalizedId}/upload`, formData, {
          headers: headers,
          onUploadProgress: onProgress,
        });
      }
      const headers = { 'content-type': 'multipart/form-data' };
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('file', file);
      });

      return API.post(`/${pathPrefix}/${id}/upload`, formData, {
        headers: headers,
        onUploadProgress: onProgress,
      });
    },
  });

  const uploadUrlMutation = useMutation({
    mutationFn: async ({ pathPrefix, id, dialogValue }) => {
      if (isServicePath(pathPrefix)) {
        const normalizedId = normalizeServiceId(id);
        if (!normalizedId) {
          throw new Error('invalid service url payload');
        }
        return API.post(`/${pathPrefix}/${normalizedId}/url`, dialogValue);
      }
      return API.post(`/${pathPrefix}/${id}/url`, {
        url: dialogValue.url,
        name: dialogValue.name,
        description: dialogValue.description ?? '',
      });
    },
  });

  const updateFileMutation = useMutation({
    mutationFn: async ({ pathPrefix, id, updatedFile }) => {
      if (isServicePath(pathPrefix)) {
        const normalizedId = normalizeServiceId(id);
        const fileId = normalizeServiceId(updatedFile?._id);
        if (!normalizedId || !fileId) {
          throw new Error('invalid service file update payload');
        }
        const payload = {
          name: typeof updatedFile?.name === 'string' ? updatedFile.name : '',
          description: typeof updatedFile?.description === 'string' ? updatedFile.description : '',
        };
        if (typeof updatedFile?.path === 'string') {
          payload.path = updatedFile.path;
        }
        return API.put(`/${pathPrefix}/${normalizedId}/file/${fileId}`, payload);
      }
      return API.put(`/${pathPrefix}/${id}/file/${updatedFile._id}`, {
        name: updatedFile.name,
        description: updatedFile.description ?? '',
        path: updatedFile.path,
      });
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: async ({ pathPrefix, id, fileToDelete }) => {
      if (isServicePath(pathPrefix)) {
        const normalizedId = normalizeServiceId(id);
        const fileId = normalizeServiceId(fileToDelete?._id);
        const filename = typeof fileToDelete?.name === 'string' ? fileToDelete.name : '';
        if (!normalizedId || !fileId || !filename) {
          throw new Error('invalid service file delete payload');
        }
        const url = `/${pathPrefix}/${normalizedId}/${fileId}/${encodeURIComponent(filename)}`;
        return API.delete(url);
      }
      const url =
        pathPrefix === 'content-log'
          ? `/${pathPrefix}/${id}/${encodeURIComponent(fileToDelete.name)}`
          : `/${pathPrefix}/${id}/${fileToDelete._id}/${encodeURIComponent(fileToDelete.name)}`;
      return API.delete(encodeURI(url));
    },
  });

  const updateLiveLinkMutation = useMutation({
    mutationFn: async ({ id, updatedLiveLink }) => {
      return API.patch(`/content-log/${id}/live-link`, {
        name: updatedLiveLink.name,
        description: updatedLiveLink.description ?? '',
        path: updatedLiveLink.path,
      });
    },
  });

  const deleteLiveLinkMutation = useMutation({
    mutationFn: async ({ id }) => {
      return API.patch(`/content-log/${id}/live-link`, { isDelete: true });
    },
  });

  const invalidateFuzzySearch = () => {
    if (state?.queryString && state?.category) {
      return queryClient.invalidateQueries({
        queryKey: ['fuzzySearch', type, state.queryString, state.category],
      });
    }
    return Promise.resolve();
  };

  const onChange = (colName) => async (e) => {
    let invalidateQueryKey;
    if (type === TrackerDataType.SERVICE) {
      invalidateQueryKey = 'monthlyServices';
    } else if (type === TrackerDataType.ENGAGEMENT) {
      invalidateQueryKey = 'monthlyEngagements';
    } else if (type === TrackerDataType.CONTENT_LOG) {
      invalidateQueryKey = 'monthlyContentLogs';
    } else {
      invalidateQueryKey = '';
    }

    if (e.target.files.length) {
      const files = Object.values(e.target.files);
      setFileProgress(0);
      let uploadPathPrefix = 'engagement';
      if (type === TrackerDataType.SERVICE) {
        uploadPathPrefix = 'service';
      } else if (type === TrackerDataType.CONTENT_LOG) {
        uploadPathPrefix = 'content-log';
      }

      try {
        const response = await uploadFilesMutation.mutateAsync({
          pathPrefix: uploadPathPrefix,
          id,
          files,
          onProgress: (data) => {
            setFileProgress(Math.round((100 * data.loaded) / data.total));
          },
        });

        if (response.status !== 201) {
          alert(response.statusText);
        } else {
          await queryClient.invalidateQueries({ queryKey: [invalidateQueryKey] });
          await invalidateFuzzySearch();
          dispatch(openSnackbar({ severity: 'success', message: `${colName} entry added!` }));
        }

        const filesData = response.data.files || null;
        const cellData = cells[colName].concat(filesData);

        onEdit(cellData, colName);
      } catch (err) {
        alert(`post failed - ${err}`);
      }
    }
  };

  const handleDrag = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (colName) => async (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (e.dataTransfer.files && e.dataTransfer.files[0] && isDragActive) {
      const files = Object.values(e.dataTransfer.files);

      setFileProgress(0);
      let uploadPathPrefix = 'service';
      if (type === TrackerDataType.ENGAGEMENT) {
        uploadPathPrefix = 'engagement';
      } else if (type === TrackerDataType.CONTENT_LOG) {
        uploadPathPrefix = 'content-log';
      }

      try {
        const response = await uploadFilesMutation.mutateAsync({
          pathPrefix: uploadPathPrefix,
          id,
          files,
          onProgress: (data) => {
            setFileProgress(Math.round((100 * data.loaded) / data.total));
          },
        });

        if (response.status !== 201) {
          alert(response.statusText);
        } else {
          await invalidateFuzzySearch();
          dispatch(openSnackbar({ severity: 'success', message: `${colName} entry added!` }));
        }

        const filesData = response.data.files || null;
        const cellData = cells[colName].concat(filesData);

        onEdit(cellData, colName);
      } catch (err) {
        alert(`post failed - ${err}`);
      }
    }
    setDragActive(false);
  };

  const handleSubmit = (callback, dialogValue, handleClose, colName, id, dispatch) => async (event) => {
    event.preventDefault();

    let pathPrefix;
    let invalidateQueryKey;
    switch (type) {
      case TrackerDataType.SERVICE:
        pathPrefix = 'service';
        invalidateQueryKey = 'monthlyServices';
        break;
      case TrackerDataType.MEDIA_INTERACTION:
        pathPrefix = 'issues';
        invalidateQueryKey = 'monthlyIssues';
        break;
      case TrackerDataType.ENGAGEMENT:
        pathPrefix = 'engagement';
        invalidateQueryKey = 'monthlyEngagements';
        break;
      case TrackerDataType.CONTENT_LOG:
        pathPrefix = 'content-log';
        invalidateQueryKey = 'monthlyContentLogs';
        break;
      default:
        pathPrefix = 'service';
        invalidateQueryKey = 'monthlyServices';
        break;
    }

    handleClose();

    try {
      const response = await uploadUrlMutation.mutateAsync({ pathPrefix, id, dialogValue });
      if (response.status !== 201) {
        alert(response.statusText);
      } else {
        queryClient.invalidateQueries({ queryKey: [invalidateQueryKey] });
        invalidateFuzzySearch();
        dispatch(openSnackbar({ severity: 'success', message: `${colName} entry added!` }));
      }
      callback(response, colName);
    } catch (e) {
      console.log('post failed - ', e);
    }
  };

  const handleCommsChange = (response, colName) => {
    const filesData =
      response.data.updatedService?.file ||
      response.data.updatedIssue?.file ||
      response.data.updatedEngagement?.file ||
      response.data.updatedContentLog?.files ||
      null;

    onEdit(filesData, colName);
  };

  const [openForm, setOpenForm] = useState(false);
  const [openSecondaryForm, setOpenSecondaryForm] = useState(false);
  const [openTertiaryForm, setOpenTertiaryForm] = useState(false);

  const handleOpenEditModal = (file, colName) => {
    setCurrentFileToEdit(file);
    setCurrentEditColumn(colName ?? null);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentFileToEdit(null);
    setCurrentEditColumn(null);
  };

  const handleSaveCommsMaterialItem = async (updatedFile) => {
    let pathPrefix;
    let invalidateQueryKey;
    switch (type) {
      case TrackerDataType.SERVICE:
        pathPrefix = 'service';
        invalidateQueryKey = 'monthlyServices';
        break;
      case TrackerDataType.MEDIA_INTERACTION:
        pathPrefix = 'issues';
        invalidateQueryKey = 'monthlyIssues';
        break;
      case TrackerDataType.ENGAGEMENT:
        pathPrefix = 'engagement';
        invalidateQueryKey = 'monthlyEngagements';
        break;
      case TrackerDataType.CONTENT_LOG:
        pathPrefix = 'content-log';
        invalidateQueryKey = 'monthlyContentLogs';
        break;
      default:
        dispatch(openSnackbar({ severity: 'error', message: 'Unknown data type for saving file.' }));
        return;
    }

    try {
      const response = await updateFileMutation.mutateAsync({ pathPrefix, id, updatedFile });

      if (response.status === 200) {
        await queryClient.invalidateQueries({ queryKey: [invalidateQueryKey] });
        dispatch(openSnackbar({ severity: 'success', message: 'File details updated!' }));
        const updatedFiles =
          response.data.updatedService?.file ||
          response.data.updatedIssue?.file ||
          response.data.updatedEngagement?.file ||
          response.data.updatedContentLog?.files ||
          [];
        let fallbackFileColName = ContentLogKeys.file;
        if (type === TrackerDataType.SERVICE) {
          fallbackFileColName = ServiceKeys.file;
        } else if (type === TrackerDataType.MEDIA_INTERACTION) {
          fallbackFileColName = IssueKeys.file;
        } else if (type === TrackerDataType.ENGAGEMENT) {
          fallbackFileColName = EngagementKeys.file;
        }

        onEdit(
          updatedFiles,
          updatedFile.colName || fallbackFileColName
        );
        handleCloseEditModal();
      } else {
        dispatch(openSnackbar({ severity: 'error', message: `Failed to update file: ${response.statusText}` }));
      }
    } catch (e) {
      console.error('Failed to update file', e);
      dispatch(openSnackbar({ severity: 'error', message: 'An error occurred while updating the file.' }));
    }
  };

  const handleDeleteCommsMaterialItem = async (fileToDelete) => {
    let pathPrefix;
    let cellNameToUpdate;
    let invalidateQueryKey;
    switch (type) {
      case TrackerDataType.SERVICE:
        pathPrefix = 'service';
        cellNameToUpdate = ServiceKeys.file;
        invalidateQueryKey = 'monthlyServices';
        break;
      case TrackerDataType.MEDIA_INTERACTION:
        pathPrefix = 'issues';
        cellNameToUpdate = IssueKeys.file;
        invalidateQueryKey = 'monthlyIssues';
        break;
      case TrackerDataType.ENGAGEMENT:
        pathPrefix = 'engagement';
        cellNameToUpdate = EngagementKeys.file;
        invalidateQueryKey = 'monthlyEngagements';
        break;
      case TrackerDataType.CONTENT_LOG:
        pathPrefix = 'content-log';
        cellNameToUpdate = ContentLogKeys.file;
        invalidateQueryKey = 'monthlyContentLogs';
        break;
      default:
        dispatch(openSnackbar({ severity: 'error', message: 'Unknown data type for deleting file.' }));
        return;
    }

    try {
      const response = await deleteFileMutation.mutateAsync({ pathPrefix, id, fileToDelete });

      if (response.status === 204) {
        await queryClient.invalidateQueries({ queryKey: [invalidateQueryKey] });
        dispatch(openSnackbar({ severity: 'warning', message: `"${fileToDelete.name}" deleted!` }));
        const currentFiles = cells[cellNameToUpdate] || [];
        const updatedFiles = currentFiles.filter((f) => f._id !== fileToDelete._id);
        onEdit(updatedFiles, cellNameToUpdate);
        handleCloseEditModal();
      } else {
        dispatch(openSnackbar({ severity: 'error', message: `Failed to delete file: ${response.statusText}` }));
      }
    } catch (e) {
      console.error('Failed to delete file', e);
      dispatch(openSnackbar({ severity: 'error', message: 'An error occurred while deleting the file.' }));
    }
  };

  const handleSaveLiveLinkItem = async (updatedLiveLink) => {
    if (type !== TrackerDataType.CONTENT_LOG) {
      dispatch(openSnackbar({ severity: 'error', message: 'Live Link is only supported for Content Log.' }));
      return;
    }

    try {
      const response = await updateLiveLinkMutation.mutateAsync({ id, updatedLiveLink });
      if (response.status === 200) {
        await queryClient.invalidateQueries({ queryKey: ['monthlyContentLogs'] });
        await invalidateFuzzySearch();
        dispatch(openSnackbar({ severity: 'success', message: 'Live Link updated!' }));
        onEdit(response.data.updatedContentLog?.liveLink ?? null, ContentLogKeys.liveLink);
        handleCloseEditModal();
      } else {
        dispatch(openSnackbar({ severity: 'error', message: `Failed to update live link: ${response.statusText}` }));
      }
    } catch (e) {
      console.error('Failed to update live link', e);
      dispatch(openSnackbar({ severity: 'error', message: 'An error occurred while updating the live link.' }));
    }
  };

  const handleDeleteLiveLinkItem = async () => {
    if (type !== TrackerDataType.CONTENT_LOG) {
      dispatch(openSnackbar({ severity: 'error', message: 'Live Link is only supported for Content Log.' }));
      return;
    }

    try {
      const response = await deleteLiveLinkMutation.mutateAsync({ id });
      if (response.status === 200) {
        await queryClient.invalidateQueries({ queryKey: ['monthlyContentLogs'] });
        await invalidateFuzzySearch();
        dispatch(openSnackbar({ severity: 'warning', message: 'Live Link deleted!' }));
        onEdit(null, ContentLogKeys.liveLink);
        handleCloseEditModal();
      } else {
        dispatch(openSnackbar({ severity: 'error', message: `Failed to delete live link: ${response.statusText}` }));
      }
    } catch (e) {
      console.error('Failed to delete live link', e);
      dispatch(openSnackbar({ severity: 'error', message: 'An error occurred while deleting the live link.' }));
    }
  };

  const renderHiddenCell = ({ name, editSx, sx }) => {
    const hasLiveLink = Boolean(cells?.[ContentLogKeys.liveLink]);
    let displayName = name;
    if (name === IssueKeys.contactMethod || name === EngagementKeys.contactMethod) {
      displayName = 'Mode of Communication';
    } else if (name === ContentLogKeys.linkedService) {
      displayName = 'Linked Service';
    }

    let hiddenCardTitle = displayName;
    if (displayName === ServiceKeys.linkedInteraction && userTenantHasContentLogAddOn) {
      hiddenCardTitle = 'Interactions, Engagements, Contents';
    } else if (displayName === ServiceKeys.linkedInteraction && userTenantHasEngagementAddOn) {
      hiddenCardTitle = 'Interactions and Engagements';
    }

    const isFileCell =
      name === ServiceKeys.file ||
      name === IssueKeys.file ||
      name === EngagementKeys.file ||
      name === ContentLogKeys.file;
    const shouldShowFileUpload =
      name !== ContentLogKeys.liveLink &&
      (type === TrackerDataType.SERVICE ||
        type === TrackerDataType.ENGAGEMENT ||
        type === TrackerDataType.CONTENT_LOG);
    const shouldShowLiveLinkUpload = name === ContentLogKeys.liveLink && type === TrackerDataType.CONTENT_LOG;

    let hiddenCardActions = null;
    if (isFileCell) {
      hiddenCardActions = (
        <>
          <UrlDialog
            colName={name}
            type={type}
            id={id}
            isOpen={isUrlFormOpen}
            setIsOpen={setIsUrlFormOpen}
            callback={handleCommsChange}
            handleSubmit={handleSubmit}
          />
          {shouldShowFileUpload ? <FileUploadButton file={file} onChange={onChange(name)} /> : null}
          <UrlUploadButton setFormOpen={setIsUrlFormOpen} />
        </>
      );
    } else if (shouldShowLiveLinkUpload) {
      hiddenCardActions = (
        <LiveLinkUploadButton
          disabled={hasLiveLink}
          onAdd={() => handleOpenEditModal(null, ContentLogKeys.liveLink)}
        />
      );
    }

    const hiddenCellCard = (
      <Card className={classes.hiddenCellCard}>
        <CardContent className={classes.cardContent}>
          <Box className={classes.cardName}>
            {hiddenCardTitle}
            {hiddenCardActions}
          </Box>
          <Box style={{ padding: 6, minHeight: '5vh' }}>
            <FileProgressContext.Provider value={fileProgress}>
              {isEditMode ? (
                <CellSelector
                  cells={cells}
                  callBack={onEdit}
                  col={name}
                  dropdownTable={dropdownTable}
                  classes={classes}
                  id={id}
                  type={type}
                  handleDrag={handleDrag}
                  handleDrop={handleDrop(name)}
                  isDragActive={isDragActive}
                  onEditItem={handleOpenEditModal}
                />
              ) : (
                <CellViewer
                  type={type}
                  cells={cells}
                  index={name}
                  callback={onEdit}
                  dropdownTable={dropdownTable}
                  openForm={() => setOpenForm(true)}
                  openSecondaryForm={() => setOpenSecondaryForm(true)}
                  openTertiaryForm={() => setOpenTertiaryForm(true)}
                  classes={classes}
                  id={id}
                  handleDrag={handleDrag}
                  handleDrop={handleDrop(name)}
                  isDragActive={isDragActive}
                  onEditItem={handleOpenEditModal}
                />
              )}
            </FileProgressContext.Provider>
          </Box>
        </CardContent>
      </Card>
    );

    // const wrapperSx = isEditMode ? editSx : sx;
    return (
      <Box key={`${type}-${name}`} sx={sx}>
        {hiddenCellCard}
      </Box>
    );
  };

  return (
    <>
      <TableRow style={{ display: open ? 'table-row' : 'none', padding: '4px' }}>
        <TableCell colSpan={colSpan} style={{ padding: '10px', paddingLeft: '20px', paddingRight: '15px' }}>
          <Collapse in={open} timeout={250} classes={{ wrapperInner: classes.collapseWrapperInner }}>
            <Box className={classes.hiddenColsContainer}>{hiddenColsMap[type].map((col) => renderHiddenCell(col))}</Box>
          </Collapse>
        </TableCell>
      </TableRow>
      {linkedEventHandler && (
        <QueryAdderDrawer openForm={openForm} setOpenForm={setOpenForm}>
          <QueryAdder
            title={linkedEventHandler?.newItemModalTitle}
            dropdownTable={dropdownTable}
            setOpenForm={setOpenForm}
            eventHandler={linkedEventHandler}
            cells={processCellsForAutoComplete(cells, dropdownTable, id)}
            issueId={id}
            linkedType={type}
          />
        </QueryAdderDrawer>
      )}
      {secondaryLinkedEventHandler && (
        <QueryAdderDrawer openForm={openSecondaryForm} setOpenForm={setOpenSecondaryForm}>
          <QueryAdder
            title={secondaryLinkedEventHandler?.newItemModalTitle}
            dropdownTable={dropdownTable}
            setOpenForm={setOpenSecondaryForm}
            eventHandler={secondaryLinkedEventHandler}
            cells={processCellsForAutoComplete(cells, dropdownTable, id)}
            issueId={id}
            linkedType={type}
          />
        </QueryAdderDrawer>
      )}
      {tertiaryLinkedEventHandler && (
        <QueryAdderDrawer openForm={openTertiaryForm} setOpenForm={setOpenTertiaryForm}>
          <QueryAdder
            title={'Add a New Content Log'}
            dropdownTable={dropdownTable}
            setOpenForm={setOpenTertiaryForm}
            eventHandler={tertiaryLinkedEventHandler}
            cells={processCellsForAutoComplete(cells, dropdownTable, id)}
            issueId={id}
            linkedType={type}
          />
        </QueryAdderDrawer>
      )}
      {type === TrackerDataType.CONTENT_LOG && currentEditColumn === ContentLogKeys.liveLink ? (
        <EditCollapsedLiveLinkDialog
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          liveLinkData={currentFileToEdit}
          onSaveItem={handleSaveLiveLinkItem}
          onDeleteItem={handleDeleteLiveLinkItem}
        />
      ) : (
        <EditCommsMaterialDialog
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          fileData={currentFileToEdit}
          onSaveItem={handleSaveCommsMaterialItem}
          onDeleteItem={handleDeleteCommsMaterialItem}
          type={type}
        />
      )}
    </>
  );
};

TrackerCollapsedRow.propTypes = {
  cells: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  colSpan: PropTypes.number.isRequired,
  dropdownTable: PropTypes.object.isRequired,
  isEditMode: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  type: PropTypes.oneOf([
    TrackerDataType.MEDIA_INTERACTION,
    TrackerDataType.SERVICE,
    TrackerDataType.ENGAGEMENT,
    TrackerDataType.CONTENT_LOG,
  ]).isRequired,
  linkedEventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]),
    newItemModalTitle: PropTypes.string,
  }),
  secondaryLinkedEventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]),
    newItemModalTitle: PropTypes.string,
  }),
  tertiaryLinkedEventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]),
    newItemModalTitle: PropTypes.string,
  }),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default TrackerCollapsedRow;
